<script setup>
// OS NÚMEROS DA CAIXA ÚNICA (fatia 4, 20/10/2026), só para quem administra: quanto se decidiu, em quanto
// tempo e quanto venceu sem ninguém, por tipo e por pessoa — do livro de decisões (Advup::Pendencias::Numeros).
// E o substituto do escritório: quem recebe o que passou do prazo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import pendenciasAPI from 'dashboard/api/pendencias';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import { moedaComSimbolo } from 'dashboard/helper/moeda';

const { t } = useI18n();
const P = 'PENDENCIAS';

const numeros = ref(null);
const ajustes = ref(null);
const carregando = ref(true);
const salvando = ref(false);
const falhou = ref(false);

const carregar = async () => {
  carregando.value = true;
  falhou.value = false;
  try {
    const [n, a] = await Promise.all([
      pendenciasAPI.numeros(30),
      pendenciasAPI.ajustes(),
    ]);
    numeros.value = n.data;
    ajustes.value = a.data;
  } catch {
    // Sem os ajustes, o seletor mostraria "Ninguém" e o próximo clique apagaria o substituto: não mostra.
    falhou.value = true;
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const opcoes = computed(() => [
  { value: null, label: t(`${P}.SUBSTITUTO_NINGUEM`) },
  ...(ajustes.value?.administradores || []).map(a => ({
    value: a.id,
    label: a.nome,
  })),
]);

// A triagem (23/10/2026): quem recebe o que não tem dono. Qualquer pessoa do escritório.
const opcoesDaTriagem = computed(() => [
  { value: null, label: t(`${P}.TRIAGEM_NINGUEM`) },
  ...(ajustes.value?.equipe || []).map(p => ({ value: p.id, label: p.nome })),
]);

const salvarTriagem = async id => {
  salvando.value = true;
  try {
    const { data } = await pendenciasAPI.salvarAjustes({ triagem_id: id });
    ajustes.value = data;
    useAlert(t(`${P}.TRIAGEM_SALVA`));
  } catch (erro) {
    useAlert(erro?.response?.data?.error || t(`${P}.ERRO_DECIDIR`));
  } finally {
    salvando.value = false;
  }
};

const salvarSubstituto = async id => {
  salvando.value = true;
  try {
    const { data } = await pendenciasAPI.salvarAjustes({ substituto_id: id });
    ajustes.value = data;
    useAlert(t(`${P}.SUBSTITUTO_SALVO`));
  } catch (erro) {
    useAlert(erro?.response?.data?.error || t(`${P}.ERRO_DECIDIR`));
  } finally {
    salvando.value = false;
  }
};

const tempo = minutos => {
  if (minutos === null || minutos === undefined) return '—';
  if (minutos < 60) return t(`${P}.MINUTOS`, { n: minutos });
  return t(`${P}.HORAS`, { n: Math.round(minutos / 60) });
};

const nomeDoTipo = tipo => t(`${P}.GRUPOS.${tipo}.TITULO`);

// HONORÁRIO RECUPERADO (fatia 5.1, 27/10/2026): quanto do que a vigia de RPV achou virou lançamento.
const honorarios = computed(() => numeros.value?.honorarios || null);
const temHonorario = computed(
  () => honorarios.value?.sugeridos > 0 || honorarios.value?.esperando > 0
);
const dinheiro = valor => moedaComSimbolo(valor) || moedaComSimbolo(0);
const diasAteDecidir = n =>
  n === null || n === undefined ? '—' : t(`${P}.HONORARIO.DIAS`, n);
</script>

<template>
  <div class="pnum">
    <EsqueletoLista
      v-if="carregando"
      :linhas="3"
      :altura="64"
      :rotulo="t(`${P}.CARREGANDO`)"
    />

    <div v-else-if="falhou" class="advup-vazio advup-vazio--erro">
      <span class="advup-vazio__icone" aria-hidden="true">
        <i class="i-lucide-triangle-alert" />
      </span>
      <p class="advup-vazio__tit">{{ t(`${P}.ERRO`) }}</p>
      <button
        type="button"
        class="advup-btn advup-vazio__acao"
        @click="carregar"
      >
        <i class="i-lucide-rotate-ccw" aria-hidden="true" />
        {{ t(`${P}.TENTAR_DE_NOVO`) }}
      </button>
    </div>

    <template v-else>
      <section class="advup-secao pnum__bloco">
        <h3 class="advup-secao__tit">{{ t(`${P}.TRIAGEM`) }}</h3>
        <p class="pnum__dica">{{ t(`${P}.TRIAGEM_DICA`) }}</p>
        <AdvupSelect
          :model-value="ajustes?.triagem_id ?? null"
          :options="opcoesDaTriagem"
          :disabled="salvando"
          @update:model-value="salvarTriagem"
        />
      </section>

      <section class="advup-secao pnum__bloco">
        <h3 class="advup-secao__tit">{{ t(`${P}.SUBSTITUTO`) }}</h3>
        <p class="pnum__dica">{{ t(`${P}.SUBSTITUTO_DICA`) }}</p>
        <AdvupSelect
          :model-value="ajustes?.substituto_id ?? null"
          :options="opcoes"
          :disabled="salvando"
          @update:model-value="salvarSubstituto"
        />
      </section>

      <section class="advup-secao pnum__bloco">
        <h3 class="advup-secao__tit">{{ t(`${P}.NUMEROS`) }}</h3>
        <p class="pnum__dica">{{ t(`${P}.NUMEROS_SUB`) }}</p>

        <p v-if="!numeros?.total" class="pnum__vazio">
          {{ t(`${P}.NUMEROS_VAZIO`) }}
        </p>

        <template v-else>
          <div class="pnum__kpis">
            <div class="pnum__kpi">
              <p class="pnum__valor">{{ numeros.geral.decididas }}</p>
              <p class="pnum__rot">{{ t(`${P}.NUMEROS_DECIDIDAS`) }}</p>
            </div>
            <div class="pnum__kpi">
              <p class="pnum__valor">
                {{ tempo(numeros.geral.mediana_minutos) }}
              </p>
              <p class="pnum__rot">{{ t(`${P}.NUMEROS_MEDIANA`) }}</p>
            </div>
            <div
              class="pnum__kpi"
              :class="{ 'pnum__kpi--alerta': numeros.geral.venceram > 0 }"
            >
              <p class="pnum__valor">{{ numeros.geral.venceram }}</p>
              <p class="pnum__rot">{{ t(`${P}.NUMEROS_VENCERAM`) }}</p>
            </div>
          </div>

          <ul class="pnum__lista">
            <li
              v-for="linha in numeros.por_tipo"
              :key="linha.tipo"
              class="pnum__linha"
            >
              <p class="pnum__nome">{{ nomeDoTipo(linha.tipo) }}</p>
              <dl class="pnum__medidas">
                <div>
                  <dd>{{ linha.decididas }}</dd>
                  <dt>{{ t(`${P}.NUMEROS_DECIDIDAS`) }}</dt>
                </div>
                <div>
                  <dd>{{ tempo(linha.mediana_minutos) }}</dd>
                  <dt>{{ t(`${P}.NUMEROS_MEDIANA`) }}</dt>
                </div>
                <div>
                  <dd :class="{ pnum__alerta: linha.venceram > 0 }">
                    {{ linha.venceram }}
                  </dd>
                  <dt>{{ t(`${P}.NUMEROS_VENCERAM`) }}</dt>
                </div>
                <div v-tooltip.top="t(`${P}.NUMEROS_RAPIDAS_DICA`)">
                  <dd>{{ linha.rapidas }}</dd>
                  <dt>{{ t(`${P}.NUMEROS_RAPIDAS`) }}</dt>
                </div>
              </dl>
            </li>
          </ul>

          <h4 class="pnum__sub">{{ t(`${P}.NUMEROS_PESSOA`) }}</h4>
          <ul class="pnum__lista">
            <li
              v-for="pessoa in numeros.por_pessoa"
              :key="pessoa.id"
              class="pnum__linha pnum__linha--pessoa"
            >
              <p class="pnum__nome">{{ pessoa.nome }}</p>
              <p class="pnum__resumo">
                {{
                  t(`${P}.NUMEROS_PESSOA_RESUMO`, {
                    n: pessoa.decididas,
                    tempo: tempo(pessoa.mediana_minutos),
                  })
                }}
              </p>
            </li>
          </ul>
        </template>
      </section>
      <section v-if="honorarios" class="advup-secao pnum__bloco">
        <h3 class="advup-secao__tit">{{ t(`${P}.HONORARIO.TITULO`) }}</h3>
        <p class="pnum__dica">{{ t(`${P}.HONORARIO.SUB`) }}</p>

        <p v-if="!temHonorario" class="pnum__vazio">
          {{ t(`${P}.HONORARIO.VAZIO`) }}
        </p>

        <template v-else>
          <div class="pnum__kpis pnum__kpis--dinheiro">
            <div class="pnum__kpi">
              <p class="pnum__valor">
                {{ dinheiro(honorarios.valor_lancado) }}
              </p>
              <p class="pnum__rot">
                {{ t(`${P}.HONORARIO.LANCADO`, honorarios.lancados) }}
              </p>
            </div>
            <div
              class="pnum__kpi"
              :class="{ 'pnum__kpi--alerta': honorarios.esperando > 0 }"
            >
              <p class="pnum__valor">
                {{ dinheiro(honorarios.valor_esperando) }}
              </p>
              <p class="pnum__rot">
                {{ t(`${P}.HONORARIO.ESPERANDO`, honorarios.esperando) }}
              </p>
            </div>
            <div class="pnum__kpi">
              <p class="pnum__valor">
                {{ diasAteDecidir(honorarios.mediana_dias) }}
              </p>
              <p class="pnum__rot">{{ t(`${P}.NUMEROS_MEDIANA`) }}</p>
            </div>
          </div>
          <p class="pnum__dica">
            {{
              t(`${P}.HONORARIO.RESUMO`, {
                sugeridos: honorarios.sugeridos,
                lancados: honorarios.lancados,
                recusados: honorarios.recusados,
              })
            }}
            <template v-if="honorarios.sem_valor">
              {{ t(`${P}.HONORARIO.SEM_VALOR`, honorarios.sem_valor) }}
            </template>
          </p>
        </template>
      </section>
    </template>
  </div>
</template>

<style scoped>
.pnum {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}

.pnum__bloco {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.pnum__dica,
.pnum__vazio {
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
}

.pnum__sub {
  margin: var(--esp-3) 0 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
}

.pnum__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--esp-3);
}

.pnum__kpi {
  padding: var(--esp-4);
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-4));
}

.pnum__kpi--alerta {
  background: rgb(var(--amber-2));
  box-shadow: inset 0 0 0 1px rgb(var(--amber-5));
}

.pnum__valor {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-titulo);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
}

.pnum__kpi--alerta .pnum__valor {
  color: rgb(var(--amber-11));
}

.pnum__rot {
  margin: 2px 0 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
}

.pnum__lista {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
}

.pnum__linha {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: var(--esp-3) var(--esp-4);
}

.pnum__linha + .pnum__linha {
  border-top: 1px solid rgb(var(--slate-4));
}

.pnum__linha--pessoa {
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
}

.pnum__nome {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-corpo);
  font-weight: var(--peso-titulo);
}

.pnum__resumo {
  margin: 0;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
}

.pnum__medidas {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--esp-2);
  margin: 0;
}

.pnum__medidas > div {
  display: flex;
  flex-direction: column;
}

.pnum__medidas dt {
  margin-top: 2px;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
}

.pnum__medidas dd {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
}

.pnum__medidas dd.pnum__alerta {
  color: rgb(var(--amber-11));
  font-weight: var(--peso-forte);
}

/* Dinheiro não quebra no meio ("R$" sozinho numa linha): no celular os três cartões empilham. */
.pnum__kpis--dinheiro .pnum__valor {
  white-space: nowrap;
}

@media (max-width: 639px) {
  .pnum__medidas {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pnum__kpis--dinheiro {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
